// Boot: fonts → renderer → React overlay. First pull within 5 s of pressing WAKE UP (docs/03 §4).
import { createRoot } from 'react-dom/client';
import './ui/pixel.css';
import { audio } from './audio/audio';
import { getRenderer, startRenderer } from './render';
import { setUI, store, TEST_MODE } from './state/store';
import { Decimal } from './sim/decimal';
import type { GameState } from './sim/types';
import { App } from './ui/App';
import { installGamepad } from './ui/gamepad';
import { installIdleWatcher } from './ui/idle';
import { platform } from './platform/platform';
import { installKeyboard } from './ui/keyboard';

async function loadFonts(): Promise<void> {
  const fonts = [
    new FontFace('m5x7', 'url(fonts/m5x7.ttf)'),
    new FontFace('m6x11', 'url(fonts/m6x11plus.woff2)'),
    new FontFace('Pixelify', 'url(fonts/PixelifySans.ttf)'),
  ];
  await Promise.all(
    fonts.map((f) =>
      f
        .load()
        .then((ff) => document.fonts.add(ff))
        .catch((e) => console.warn('font failed', e)),
    ),
  );
}

async function boot(): Promise<void> {
  await loadFonts();
  const stageEl = document.getElementById('stage')!;
  const host = document.getElementById('canvas-host')!;
  await startRenderer(stageEl, host);
  const s = store.getState().game.meta.settings;
  audio.setVolumes({ master: s.master, music: s.music, sfx: s.sfx });
  installKeyboard();
  installIdleWatcher();
  installGamepad();
  // Pause on focus loss (docs/09 Phase 7): the Hand lets go, the jazz drops to a whisper.
  platform.onFocus((focused) => {
    if (focused) {
      audio.music.duck(0, 0);
      return;
    }
    audio.music.duck(-30, 24 * 60 * 60 * 1000);
    if (store.getState().game.meta.hand.autospin) getRenderer()?.hand.toggle();
  });
  createRoot(document.getElementById('ui')!).render(<App />);
  document.getElementById('loading')?.remove();
  const debug = TEST_MODE
    ? {
        D: (v: number | string) => new Decimal(v),
        audio,
        /** Patch the game state in place and publish it (screenshot/e2e scripts only). */
        patch: (fn: (g: GameState) => void) => {
          const g = store.getState().game;
          fn(g);
          store.setState({ game: { ...g } });
        },
      }
    : undefined;
  (window as unknown as { maw: unknown }).maw = { store, setUI, renderer: getRenderer(), debug };
}

void boot();
